import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
  Avatar,
  Paper,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Switch,
  Slider,
} from "@mui/material";
import {
  SearchOutlined,
  NotificationsOutlined,
  SettingsOutlined,
  AppsOutlined,
  HomeOutlined,
  Star,
  AccountCircleOutlined,
  LanguageOutlined,
  Fullscreen,
  FullscreenExit,
  TextFields,
  LogoutOutlined,
  Palette,
  Check,
  RestartAlt,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import ThemeSelector from "../Navigation/ThemeSelector";
import ModuleGroups from "../Navigation/ModuleGroups";
import { getThemeById } from "../../data/themeColors";
import { useNotificationStore } from "../../stores/notificationStore";

// Styled components para mantener consistencia con el diseño moderno
// Nota: Los colores se aplican dinámicamente en el componente usando sx prop y estilos inline
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  // Los colores de fondo se aplican dinámicamente, no aquí
}));

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  padding: "0 16px",
  minHeight: "64px",
});

const AppIconButton = styled(IconButton)(({ theme, color }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  margin: "0 2px",
  backgroundColor: color || "transparent",
  color: color ? "#ffffff" : "inherit",
  transition: "all 0.2s ease",
  cursor: "grab",
  "&:hover": {
    transform: "scale(1.05)",
    backgroundColor: color || theme.palette.action.hover,
  },
  "&:active": {
    cursor: "grabbing",
  },
}));

const SearchBox = styled(TextField)(({ dark }) => ({
  width: 256,
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: 8,
    backgroundColor: dark ? "#2d2d2d" : "#f5f5f5",
    color: dark ? "#ffffff" : "#1a1a1a",
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: dark ? "#555" : "#e0e0e0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
    },
  },
  "& input": {
    fontSize: 14,
    color: dark ? "#ffffff" : "#1a1a1a",
    "&::placeholder": {
      color: dark ? "#888" : "#757575",
    },
  },
}));

const NotificationDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 360,
    padding: theme.spacing(2),
  },
}));

/**
 * AdminTopNavigationBar - Barra de navegación superior para AdminConsole
 * Implementación con Material-UI (MUI) basada en el diseño moderno de TopNavigationBar
 *
 * @param {Object} props
 * @param {Array} props.pinnedApps - Aplicaciones fijadas a mostrar en la barra
 * @param {Function} props.onAppClick - Callback al hacer click en una app
 * @param {Function} props.onSearchClick - Callback al hacer click en búsqueda
 * @param {Function} props.onSettingsClick - Callback al hacer click en configuración
 * @param {Function} props.onAppLauncherClick - Callback al hacer click en launcher
 * @param {Object} props.settings - Configuración de la barra de navegación
 * @param {Array} props.notifications - Lista de notificaciones
 * @param {Function} props.onLogout - Callback para cerrar sesión
 */
const AdminTopNavigationBar = ({
  pinnedApps = [],
  onAppClick = () => {},
  onSearchClick = () => {},
  onSettingsClick = () => {},
  onAppLauncherClick = () => {},
  onHomeDashboard = () => {},
  settings: propsSettings = {
    showHomeDashboard: true,
    showSearchBox: true,
    showAppLauncher: true,
    showStarShortcut: false,
    showLanguageSelector: false,
    showFullscreenToggle: false,
    showFontSizeControl: false,
    topBarIconStyle: "colored",
    layoutStyle: "horizontal",
  },
  notifications = [],
  user = {
    name: "Admin User",
    email: "admin@example.com",
    avatar: null,
  },
  onLogout = () => {},
}) => {
  const { t } = useTranslation("common");
  // Zustand store para tema y colores - usar selectores específicos para re-renderizar cuando cambien
  const setLanguage = useAppRegistryStore((state) => state.setLanguage);
  const setFontSize = useAppRegistryStore((state) => state.setFontSize);
  const pinApp = useAppRegistryStore((state) => state.pinApp);
  const unpinApp = useAppRegistryStore((state) => state.unpinApp);
  const storeApps = useAppRegistryStore((state) => state.apps);
  const categories = useAppRegistryStore((state) => state.categories);
  const storeSettings = useAppRegistryStore((state) => state.settings);
  const selectedThemeColor = useAppRegistryStore(
    (state) => state.settings?.selectedTheme || "default",
  );

  // Usar storeSettings como fuente principal, propsSettings como fallback
  const settings = { ...storeSettings, ...propsSettings };

  const currentTheme = settings?.currentTheme || "light";
  const isDarkMode = currentTheme === "dark";

  // Obtener el tema de color seleccionado - usar useMemo para evitar recálculos innecesarios
  const themeColors = useMemo(
    () => getThemeById(selectedThemeColor),
    [selectedThemeColor],
  );

  // El modo oscuro/claro prevalece sobre el tema de color
  // Si está en modo oscuro, usar colores oscuros; si está en modo claro, usar colores del tema
  const navbarBg = isDarkMode
    ? "var(--navbar)"
    : themeColors?.colors?.navbar || "#ffffff";
  const navbarColor = isDarkMode
    ? "var(--text-primary)"
    : themeColors?.colors?.foreground || "#1a1a1a";
  const toolbarBg = isDarkMode
    ? "var(--toolbar)"
    : themeColors?.colors?.toolbar || themeColors?.colors?.navbar || "#ffffff";

  // Estados locales
  const [hoveredApp, setHoveredApp] = useState(null);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [starMenuAnchor, setStarMenuAnchor] = useState(null);
  const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(true);
  const [themeSelectorOpen, setThemeSelectorOpen] = useState(false);
  const [starSearch, setStarSearch] = useState("");

  // Alternar clase dark y usar variables CSS para colores
  useEffect(() => {}, []);

  useEffect(() => {
    // Asegurar que las variables CSS del tema estén actualizadas para Navbar/Toolbar
    if (!isDarkMode && themeColors && themeColors.colors) {
      const root = document.documentElement;
      Object.entries(themeColors.colors).forEach(([key, value]) => {
        const cssVar = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        root.style.setProperty(`--${cssVar}`, value);
      });
    }
  }, [selectedThemeColor, isDarkMode, themeColors]);

  const applyBarStyles = useCallback(() => {
    let appBar = document.querySelector(".theme-applied-navbar");
    let toolbar = document.querySelector(".theme-applied-toolbar");
    if (!appBar) appBar = document.querySelector("header.MuiAppBar-root");
    if (!toolbar) toolbar = document.querySelector("header .MuiToolbar-root");
    const bgBar = isDarkMode
      ? "#1e1e1e"
      : themeColors?.colors?.navbar || "#ffffff";
    const fgBar = isDarkMode
      ? "#ffffff"
      : themeColors?.colors?.foreground || "#1a1a1a";
    const bgToolbar = isDarkMode
      ? "#1e1e1e"
      : themeColors?.colors?.navbar ||
        themeColors?.colors?.toolbar ||
        "#ffffff";
    const fgToolbar = fgBar;
    if (appBar) {
      appBar.style.setProperty("background-color", bgBar, "important");
      appBar.style.setProperty("color", fgBar, "important");
    }
    if (toolbar) {
      toolbar.style.setProperty("background-color", bgToolbar, "important");
      toolbar.style.setProperty("color", fgToolbar, "important");
    }
  }, [isDarkMode, themeColors]);

  useEffect(() => {
    applyBarStyles();
    const t1 = setTimeout(applyBarStyles, 50);
    const t2 = setTimeout(applyBarStyles, 150);
    const t3 = setTimeout(applyBarStyles, 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [applyBarStyles, themeColors, selectedThemeColor, isDarkMode]);

  useEffect(() => {}, []);

  // NO aplicar colores aquí - dejar que ThemeSelector y AdminLayout lo manejen
  // Este useEffect solo se ejecuta cuando cambia el tema, pero no debe sobrescribir
  // los colores que ThemeSelector ya aplicó

  // Detectar tamaño de pantalla
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Layout efectivo basado en configuración
  const getActualLayout = () => {
    if (settings.layoutStyle === "auto-detect") {
      return isLargeScreen ? "horizontal" : "vertical";
    }
    return settings.layoutStyle;
  };

  const actualLayout = getActualLayout();
  const isModuleGroupsMode = settings.navigationMode === "groups";

  // Handlers para drag and drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, targetIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      setDragOverIndex(targetIndex);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reordenar apps
    const reorderedApps = [...storeApps];
    const [draggedApp] = reorderedApps.splice(draggedIndex, 1);
    reorderedApps.splice(targetIndex, 0, draggedApp);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handler para fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Contador de notificaciones no leídas
  const unreadCount = notifications.filter((n) => !n.read).length;
  const {
    settings: notificationSettings,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    addNotification,
    updateSettings,
  } = useNotificationStore();
  const [activeTab, setActiveTab] = useState("notifications");

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        updateSettings({ enableDesktopNotifications: true });
      }
    }
  };

  // Obtener color de categoría (prioridad: appColor personalizado > categoryColor personalizado > category.color > app.color)
  const getCategoryColor = (app) => {
    // 1. Verificar color específico de app personalizado
    if (storeSettings.appColors && storeSettings.appColors[app.id]) {
      return storeSettings.appColors[app.id];
    }

    // 2. Buscar la categoría de la app
    const category = categories.find((cat) => cat.id === app.categoryId);

    // 3. Verificar color personalizado de categoría
    if (
      category &&
      storeSettings.categoryColors &&
      storeSettings.categoryColors[category.id]
    ) {
      return storeSettings.categoryColors[category.id];
    }

    // 4. Usar color por defecto de categoría
    if (category && category.color) {
      return category.color;
    }

    // 5. Fallback al color de la app o gris
    return app.color || "#757575";
  };

  // Calcular espaciado dinámico para etiquetas
  const calculateSpacing = (appName) => {
    const charWidth = 8;
    const padding = 24;
    const margin = 16;
    return appName.length * charWidth + padding + margin;
  };

  // Renderizar apps de la toolbar (solo fijadas/pinned)
  const renderToolbarApps = () => {
    const visibleApps = storeApps
      .filter((app) => app.pinned && !app.hidden)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, 100);
    return visibleApps.map((app, index) => {
      const categoryColor = getCategoryColor(app);
      const isBeingDragged = draggedIndex === index;
      const isDropTarget = dragOverIndex === index && draggedIndex !== null;
      const isHovered = hoveredApp === app.id;

      // Calcular espaciado dinámico cuando se muestra una etiqueta
      const hoveredAppIndex = hoveredApp
        ? visibleApps.findIndex((a) => a.id === hoveredApp)
        : -1;
      const hoveredAppName = hoveredApp
        ? visibleApps.find((a) => a.id === hoveredApp)?.name || ""
        : "";
      const dynamicSpacing =
        hoveredApp &&
        hoveredAppIndex !== -1 &&
        hoveredAppIndex < index &&
        settings.showAppNameLabels
          ? calculateSpacing(hoveredAppName)
          : 0;

      return (
        <Box
          key={app.id}
          sx={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            transition: "transform 250ms ease-out",
            transform: `translateX(${dynamicSpacing}px) ${isDropTarget ? "scale(1.05)" : ""}`,
            opacity: isBeingDragged ? 0.5 : 1,
          }}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, index)}
        >
          {isDropTarget && (
            <Box
              sx={{
                position: "absolute",
                left: -2,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: "#1976d2",
                borderRadius: 1,
              }}
            />
          )}

          {/* Tooltip solo si showHoverTooltips está habilitado */}
          {settings.showHoverTooltips ? (
            <Tooltip
              title={`${app.name} - Arrastra para reordenar`}
              placement="bottom"
            >
              <AppIconButton
                color={
                  settings.topBarIconStyle === "colored"
                    ? categoryColor
                    : undefined
                }
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onMouseEnter={() => setHoveredApp(app.id)}
                onMouseLeave={() => setHoveredApp(null)}
                onClick={() => onAppClick(app)}
                sx={{
                  backgroundColor:
                    settings.topBarIconStyle === "colored"
                      ? categoryColor
                      : "transparent",
                }}
              >
                {app.icon ? (
                  <app.icon
                    sx={{
                      fontSize:
                        settings.topBarIconStyle === "colored" ? 22 : 20,
                    }}
                  />
                ) : (
                  <AppsOutlined
                    sx={{
                      fontSize:
                        settings.topBarIconStyle === "colored" ? 22 : 20,
                      color:
                        settings.topBarIconStyle === "colored"
                          ? "#ffffff"
                          : isDarkMode
                            ? "#e0e0e0"
                            : "#6b6b6b",
                    }}
                  />
                )}
              </AppIconButton>
            </Tooltip>
          ) : (
            <AppIconButton
              color={
                settings.topBarIconStyle === "colored"
                  ? categoryColor
                  : undefined
              }
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onMouseEnter={() => setHoveredApp(app.id)}
              onMouseLeave={() => setHoveredApp(null)}
              onClick={() => onAppClick(app)}
              sx={{
                backgroundColor:
                  settings.topBarIconStyle === "colored"
                    ? categoryColor
                    : "transparent",
              }}
            >
              {app.icon ? (
                <app.icon
                  sx={{
                    fontSize: settings.topBarIconStyle === "colored" ? 22 : 20,
                  }}
                />
              ) : (
                <AppsOutlined
                  sx={{
                    fontSize: settings.topBarIconStyle === "colored" ? 22 : 20,
                    color:
                      settings.topBarIconStyle === "colored"
                        ? "#ffffff"
                        : isDarkMode
                          ? "#e0e0e0"
                          : "#6b6b6b",
                  }}
                />
              )}
            </AppIconButton>
          )}

          {/* Etiqueta de nombre al lado del ícono cuando showAppNameLabels está habilitado */}
          {isHovered && settings.showAppNameLabels && (
            <Box
              sx={{
                position: "absolute",
                left: "48px", // 40px (ancho del botón) + 8px (margen)
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1300,
                ml: 1,
                pointerEvents: "none",
                animation: "slideInFromLeft 0.2s ease",
                "@keyframes slideInFromLeft": {
                  from: {
                    opacity: 0,
                    transform: "translateY(-50%) translateX(-8px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(-50%) translateX(0)",
                  },
                },
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: categoryColor,
                  color: "#ffffff",
                  borderRadius: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {app.name}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      );
    });
  };

  // Layout vertical (sidebar)
  if (actualLayout === "vertical") {
    return (
      <>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: 64,
            backgroundColor: isDarkMode
              ? "#1e1e1e"
              : themeColors?.colors?.navbar || "#ffffff",
            borderRight: isDarkMode
              ? "1px solid #333"
              : `1px solid ${themeColors?.colors?.border || "#e0e0e0"}`,
            display: "flex",
            flexDirection: "column",
            zIndex: 1200,
            boxShadow: isDarkMode
              ? "2px 0 4px rgba(0, 0, 0, 0.3)"
              : "2px 0 4px rgba(0, 0, 0, 0.08)",
            overflowX: "hidden",
          }}
        >
          {/* Apps section */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              p: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            {isModuleGroupsMode ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <ModuleGroups onAppClick={onAppClick} />
              </Box>
            ) : (
              <>
                {settings.showHomeDashboard && (
                  <Box sx={{ width: 48 }}>
                    <Tooltip
                      title={t("toolbar.homeDashboard")}
                      placement="right"
                    >
                      <IconButton
                        onClick={onHomeDashboard}
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          mb: 1,
                          color: isDarkMode ? "#ffffff" : "#1a1a1a",
                          "&:hover": {
                            backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                          },
                        }}
                      >
                        <HomeOutlined
                          sx={{
                            fontSize:
                              settings.topBarIconStyle === "colored" ? 22 : 20,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {storeApps
                  .filter((app) => !app.hidden)
                  .map((app, index) => {
                    const categoryColor = getCategoryColor(app);
                    return (
                      <Box key={app.id} sx={{ width: 48 }}>
                        <Tooltip title={app.name} placement="right">
                          <AppIconButton
                            color={
                              settings.topBarIconStyle === "colored"
                                ? categoryColor
                                : undefined
                            }
                            onClick={() => onAppClick(app)}
                            sx={{
                              width: 48,
                              height: 48,
                              mb: 1,
                              mx: 0,
                              display: "block",
                              backgroundColor:
                                settings.topBarIconStyle === "colored"
                                  ? categoryColor
                                  : "transparent",
                              color:
                                settings.topBarIconStyle === "colored"
                                  ? "#ffffff"
                                  : isDarkMode
                                    ? "#ffffff"
                                    : "#1a1a1a",
                              "&:hover": {
                                backgroundColor:
                                  settings.topBarIconStyle === "colored"
                                    ? categoryColor
                                    : isDarkMode
                                      ? "#2d2d2d"
                                      : "#f5f5f5",
                              },
                            }}
                          >
                            {app.icon ? (
                              <app.icon
                                sx={{
                                  fontSize:
                                    settings.topBarIconStyle === "colored"
                                      ? 22
                                      : 20,
                                }}
                              />
                            ) : (
                              <AppsOutlined
                                sx={{
                                  fontSize:
                                    settings.topBarIconStyle === "colored"
                                      ? 22
                                      : 20,
                                }}
                              />
                            )}
                          </AppIconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
              </>
            )}
          </Box>

          {/* Bottom actions */}
          <Box
            sx={{
              p: 1,
              borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
            }}
          >
            {settings.showSearchBox && (
              <Tooltip title="Buscar" placement="right">
                <IconButton
                  onClick={onSearchClick}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    mb: 1,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  <SearchOutlined sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Notificaciones" placement="right">
              <IconButton
                onClick={() => setNotificationDrawerOpen(true)}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  mb: 1,
                  color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsOutlined />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Configuración" placement="right">
              <IconButton
                onClick={onSettingsClick}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  mb: 1,
                  color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <SettingsOutlined />
              </IconButton>
            </Tooltip>

            <Tooltip title="Perfil" placement="right">
              <IconButton
                onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <AccountCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Offset para contenido */}
        <Box sx={{ ml: "64px" }} />
      </>
    );
  }

  // Layout horizontal (default)
  return (
    <>
      <StyledAppBar
        position="fixed"
        color="default"
        component="div"
        className="theme-applied-navbar"
        sx={{
          backgroundColor: `${navbarBg} !important`,
          color: `${navbarColor} !important`,
          boxShadow: isDarkMode
            ? "0 1px 3px rgba(0, 0, 0, 0.3)"
            : "0 1px 3px rgba(0, 0, 0, 0.12)",
          borderBottom: isDarkMode
            ? `1px solid ${themeColors?.colors?.border || "#333"}`
            : `1px solid ${themeColors?.colors?.border || "#e0e0e0"}`,
        }}
      >
        <StyledToolbar
          className="theme-applied-toolbar"
          sx={{
            backgroundColor: `${toolbarBg} !important`,
            color: `${navbarColor} !important`,
          }}
        >
          {/* Sección izquierda - Apps */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {settings.showHomeDashboard && (
              <Tooltip title="HOME Dashboard">
                <IconButton
                  onClick={onHomeDashboard}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    mr: 1,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  <HomeOutlined
                    sx={{
                      fontSize:
                        settings.topBarIconStyle === "colored" ? 22 : 20,
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}

            {isModuleGroupsMode ? (
              <ModuleGroups onAppClick={onAppClick} />
            ) : (
              renderToolbarApps()
            )}
          </Box>

          {/* Sección derecha - Utilidades */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {settings.showSearchBox && (
              <SearchBox
                placeholder={t("search.modulesPlaceholder")}
                variant="outlined"
                size="small"
                onClick={onSearchClick}
                dark={isDarkMode}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ fontSize: 18, color: "#757575" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            backgroundColor: "#e0e0e0",
                            borderRadius: 0.5,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#757575",
                          }}
                        >
                          ⌘
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            backgroundColor: "#e0e0e0",
                            borderRadius: 0.5,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#757575",
                          }}
                        >
                          K
                        </Typography>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {settings.showStarShortcut && (
              <Tooltip title="Favoritos">
                <IconButton
                  onClick={(e) => setStarMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  <Star sx={{ color: "#ffd700" }} />
                </IconButton>
              </Tooltip>
            )}

            {settings.showAppLauncher && (
              <Tooltip title={t("toolbar.appLauncher")}>
                <IconButton
                  onClick={onAppLauncherClick}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  <AppsOutlined sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t("toolbar.notifications")}>
              <IconButton
                onClick={() => setNotificationDrawerOpen(true)}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsOutlined sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={t("toolbar.settings")}>
              <IconButton
                onClick={onSettingsClick}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <SettingsOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Perfil">
              <IconButton
                onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
                size="small"
                sx={{
                  color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                  },
                }}
              >
                <Avatar
                  src={user.avatar}
                  alt={user.name}
                  sx={{
                    width: 32,
                    height: 32,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                  }}
                >
                  {user.name.charAt(0)}
                </Avatar>
              </IconButton>
            </Tooltip>

            {settings.showLanguageSelector && (
              <Tooltip title={t("toolbar.language")}>
                <IconButton
                  onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  <LanguageOutlined sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {settings.showFontSizeControl && (
              <Tooltip title="Font Size">
                <IconButton
                  onClick={(e) => setFontSizeMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  <TextFields sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {settings.showFullscreenToggle && (
              <Tooltip
                title={
                  isFullscreen
                    ? t("toolbar.fullscreenExit")
                    : t("toolbar.fullscreenEnter")
                }
              >
                <IconButton
                  onClick={toggleFullscreen}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                    },
                  }}
                >
                  {isFullscreen ? (
                    <FullscreenExit sx={{ fontSize: 20 }} />
                  ) : (
                    <Fullscreen sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </StyledToolbar>
      </StyledAppBar>

      {/* Offset para contenido */}
      <Toolbar />

      {/* Menú de Perfil */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={() => setProfileMenuAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#1a1a1a",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}
          >
            {user.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? "#e0e0e0" : "text.secondary" }}
          >
            {user.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={onSettingsClick}>
          <ListItemIcon>
            <SettingsOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Configuración</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setThemeSelectorOpen(true);
            setProfileMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <Palette fontSize="small" />
          </ListItemIcon>
          <ListItemText>Colores de Tema</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={onLogout} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <LogoutOutlined fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>
      </Menu>

      {/* Menú de Idioma */}
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={() => setLanguageMenuAnchor(null)}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#1a1a1a",
          },
        }}
      >
        <MenuItem
          selected={settings.selectedLanguage === "en"}
          onClick={() => {
            setLanguage("en");
            setLanguageMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <span style={{ fontSize: 18 }}>🇺🇸</span>
          </ListItemIcon>
          <ListItemText>English</ListItemText>
          {settings.selectedLanguage === "en" && (
            <Check fontSize="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
        <MenuItem
          selected={settings.selectedLanguage === "es"}
          onClick={() => {
            setLanguage("es");
            setLanguageMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <span style={{ fontSize: 18 }}>🇲🇽</span>
          </ListItemIcon>
          <ListItemText>Español</ListItemText>
          {settings.selectedLanguage === "es" && (
            <Check fontSize="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={fontSizeMenuAnchor}
        open={Boolean(fontSizeMenuAnchor)}
        onClose={() => setFontSizeMenuAnchor(null)}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#1a1a1a",
            p: 2,
            width: 380,
            borderRadius: 2,
            boxShadow: isDarkMode
              ? "0 8px 24px rgba(0,0,0,0.5)"
              : "0 8px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <TextFields sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2">Font Size</Typography>
        </Box>
        <Box sx={{ px: 3 }}>
          <Slider
            value={storeSettings.fontSize || 100}
            min={70}
            max={130}
            step={10}
            marks={[
              { value: 70, label: "70%" },
              { value: 80, label: "80%" },
              { value: 90, label: "90%" },
              { value: 100, label: "100%" },
              { value: 110, label: "110%" },
              { value: 120, label: "120%" },
              { value: 130, label: "130%" },
            ]}
            onChange={(_, v) => setFontSize(Array.isArray(v) ? v[0] : v)}
            sx={{ mr: 3, ml: "0px" }}
          />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 1, pr: 2 }}
          >
            <Button
              size="small"
              variant="text"
              startIcon={<RestartAlt />}
              onClick={() => setFontSize(100)}
            >
              Reset 100%
            </Button>
          </Box>
        </Box>
      </Menu>

      <Menu
        anchorEl={starMenuAnchor}
        open={Boolean(starMenuAnchor)}
        onClose={() => setStarMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#1a1a1a",
            width: 360,
            boxShadow: isDarkMode
              ? "0 8px 24px rgba(0,0,0,0.5)"
              : "0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            value={starSearch}
            onChange={(e) => setStarSearch(e.target.value)}
            size="small"
            placeholder={t("search.modulesPlaceholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#1976d2" },
                "&:hover fieldset": { borderColor: "#1976d2" },
                "&.Mui-focused fieldset": { borderColor: "#1976d2" },
              },
            }}
            fullWidth
          />
        </Box>
        <Divider />
        <Box sx={{ p: 1 }}>
          {(starSearch
            ? storeApps.filter((a) =>
                a.name.toLowerCase().includes(starSearch.toLowerCase()),
              )
            : storeApps.slice(0, 8)
          ).map((app) => (
            <MenuItem
              key={app.id}
              onClick={() => {
                onAppClick(app);
                setStarMenuAnchor(null);
              }}
              dense
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1,
                  color: "#ffffff",
                  backgroundColor: getCategoryColor(app),
                }}
              >
                {app.icon ? (
                  <app.icon sx={{ fontSize: 16 }} />
                ) : (
                  <AppsOutlined sx={{ fontSize: 16 }} />
                )}
              </Box>
              <ListItemText
                primary={app.name}
                sx={{ mr: 0.5 }}
                primaryTypographyProps={{ noWrap: true }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const isPinned = Boolean(app.pinned);
                  const currentPinnedCount = storeApps.filter(
                    (a) => a.pinned,
                  ).length;
                  if (!isPinned && currentPinnedCount >= 100) {
                    alert(
                      "Maximum of 100 apps can be pinned to the top navigation bar.",
                    );
                    return;
                  }
                  isPinned ? unpinApp(app.id) : pinApp(app.id);
                }}
                sx={{ ml: 0.5 }}
              >
                <Star sx={{ color: app.pinned ? "#ffd700" : "#9e9e9e" }} />
              </IconButton>
            </MenuItem>
          ))}
        </Box>
      </Menu>

      <NotificationDrawer
        anchor="right"
        open={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#1a1a1a",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <NotificationsOutlined />
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}
            >
              {t("notifications.title")}
            </Typography>
            {unreadCount > 0 && (
              <Badge color="error" badgeContent={unreadCount} sx={{ ml: 1 }} />
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setNotificationDrawerOpen(false)}
            sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}
          >
            ✕
          </IconButton>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Notificaciones" value="notifications" />
          <Tab label="Configuración" value="settings" />
        </Tabs>

        {activeTab === "notifications" ? (
          <Box sx={{ mt: 2 }}>
            {notifications.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {unreadCount > 0 && (
                    <Button variant="text" size="small" onClick={markAllAsRead}>
                      Marcar todo como leído
                    </Button>
                  )}
                </Box>
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={clearAllNotifications}
                >
                  Limpiar todo
                </Button>
              </Box>
            )}

            {notifications.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <NotificationsOutlined
                  sx={{
                    fontSize: 48,
                    color: isDarkMode ? "#90caf9" : "#bdbdbd",
                    mb: 2,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: isDarkMode ? "#e0e0e0" : "text.secondary" }}
                >
                  No hay notificaciones
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const examples = [
                      {
                        type: "info",
                        title: "Bienvenido a ACV.APP",
                        message: "Tu sistema de navegación está listo.",
                        priority: "medium",
                      },
                      {
                        type: "success",
                        title: "Preferencias guardadas",
                        message: "Configuración actualizada.",
                        priority: "low",
                      },
                      {
                        type: "warning",
                        title: "Mantenimiento del sistema",
                        message: "Mantenimiento programado esta noche.",
                        priority: "high",
                      },
                      {
                        type: "error",
                        title: "Problema de conexión",
                        message: "No se puede sincronizar datos.",
                        priority: "high",
                        actionLabel: "Reintentar",
                        actionUrl: "#",
                      },
                    ];
                    const randomExample =
                      examples[Math.floor(Math.random() * examples.length)];
                    addNotification(randomExample);
                  }}
                  sx={{ mt: 2 }}
                >
                  Crear ejemplo de notificación
                </Button>
              </Box>
            ) : (
              <List>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      backgroundColor: notification.read
                        ? "transparent"
                        : isDarkMode
                          ? "rgba(25, 118, 210, 0.2)"
                          : "#e3f2fd",
                      borderRadius: 1,
                      mb: 1,
                      alignItems: "flex-start",
                    }}
                  >
                    <ListItemText
                      primary={notification.title}
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDarkMode ? "#e0e0e0" : "text.secondary",
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode
                                  ? "#e0e0e0"
                                  : "text.secondary",
                              }}
                            >
                              {formatTimestamp(notification.timestamp)}
                            </Typography>
                            {notification.priority === "high" && (
                              <Badge color="error" badgeContent={"Alta"} />
                            )}
                          </Box>
                        </>
                      }
                      primaryTypographyProps={{
                        fontWeight: notification.read ? 400 : 600,
                        sx: { color: isDarkMode ? "#ffffff" : "#1a1a1a" },
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
                      {!notification.read && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => markAsRead(notification.id)}
                        >
                          Marcar leído
                        </Button>
                      )}
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => deleteNotification(notification.id)}
                      >
                        Eliminar
                      </Button>
                      {notification.actionUrl && notification.actionLabel && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            window.open(notification.actionUrl, "_blank");
                            markAsRead(notification.id);
                          }}
                        >
                          {notification.actionLabel}
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                ))}
                <Box sx={{ p: 2 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      const examples = [
                        {
                          type: "info",
                          title: "Bienvenido a ACV.APP",
                          message: "Tu sistema de navegación está listo.",
                          priority: "medium",
                        },
                        {
                          type: "success",
                          title: "Preferencias guardadas",
                          message: "Configuración actualizada.",
                          priority: "low",
                        },
                        {
                          type: "warning",
                          title: "Mantenimiento del sistema",
                          message: "Mantenimiento programado esta noche.",
                          priority: "high",
                        },
                        {
                          type: "error",
                          title: "Problema de conexión",
                          message: "No se puede sincronizar datos.",
                          priority: "high",
                          actionLabel: "Reintentar",
                          actionUrl: "#",
                        },
                      ];
                      const randomExample =
                        examples[Math.floor(Math.random() * examples.length)];
                      addNotification(randomExample);
                    }}
                  >
                    Crear ejemplo de notificación
                  </Button>
                </Box>
              </List>
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="body2">Push Notifications</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? "#e0e0e0" : "text.secondary" }}
                >
                  Mostrar notificaciones tipo toast
                </Typography>
              </Box>
              <Switch
                checked={Boolean(notificationSettings.enablePushNotifications)}
                onChange={(e) =>
                  updateSettings({ enablePushNotifications: e.target.checked })
                }
              />
            </Box>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                my: 2,
              }}
            >
              <Box>
                <Typography variant="body2">Sound Notifications</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? "#e0e0e0" : "text.secondary" }}
                >
                  Reproducir sonido en nuevas notificaciones
                </Typography>
              </Box>
              <Switch
                checked={Boolean(notificationSettings.enableSoundNotifications)}
                onChange={(e) =>
                  updateSettings({ enableSoundNotifications: e.target.checked })
                }
              />
            </Box>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                my: 2,
              }}
            >
              <Box>
                <Typography variant="body2">Desktop Notifications</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? "#e0e0e0" : "text.secondary" }}
                >
                  Mostrar notificaciones del navegador
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {typeof Notification !== "undefined" &&
                  Notification.permission === "default" && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={requestNotificationPermission}
                    >
                      Habilitar
                    </Button>
                  )}
                <Switch
                  checked={
                    Boolean(notificationSettings.enableDesktopNotifications) &&
                    (typeof Notification === "undefined" ||
                      Notification.permission === "granted")
                  }
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    if (
                      checked &&
                      typeof Notification !== "undefined" &&
                      Notification.permission !== "granted"
                    ) {
                      await requestNotificationPermission();
                    } else {
                      updateSettings({ enableDesktopNotifications: checked });
                    }
                  }}
                  disabled={
                    typeof Notification !== "undefined" &&
                    Notification.permission === "denied"
                  }
                />
              </Box>
            </Box>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                my: 2,
              }}
            >
              <Box>
                <Typography variant="body2">Auto Mark as Read</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? "#e0e0e0" : "text.secondary" }}
                >
                  Marcar automáticamente al ver
                </Typography>
              </Box>
              <Switch
                checked={Boolean(notificationSettings.autoMarkAsRead)}
                onChange={(e) =>
                  updateSettings({ autoMarkAsRead: e.target.checked })
                }
              />
            </Box>
          </Box>
        )}
      </NotificationDrawer>

      {/* Selector de Temas */}
      <ThemeSelector
        isOpen={themeSelectorOpen}
        onClose={() => setThemeSelectorOpen(false)}
      />
    </>
  );
};

export default AdminTopNavigationBar;
